import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MdAutoAwesome,
  MdCheckCircle,
  MdClose,
  MdCompare,
  MdHome,
  MdInsights,
  MdRefresh,
  MdStorage,
  MdWarning,
} from 'react-icons/md';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import { PageSkeleton } from '@euroasiann/shared/ui';

interface FieldDiff {
  field: string;
  label: string;
  existingValue: string;
  incomingValue: string;
  severity: 'critical' | 'minor' | 'info';
  match: boolean;
}

interface Suggestion {
  _id: string;
  status: string;
  matchType: string;
  similarityScore: number;
  incoming: Record<string, string>;
  existingSnapshot: Record<string, string>;
  fieldDiffs: FieldDiff[];
  aiAnalysis?: {
    summary: string;
    recommendation: string;
    confidence: number;
    insights: string[];
    model: string;
  };
  source: { type: string; vendorName?: string; fileName?: string };
  createdAt: string;
}

interface Analytics {
  pending: number;
  accepted: number;
  rejected: number;
  identicalDismissed: number;
  avgPendingSimilarity: number;
  last7Days: number;
  topConflictFields: Array<{ field: string; count: number }>;
  bySource: Array<{ source: string; count: number }>;
}

const PIE_COLORS = ['#0088cc', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444'];

const chartTooltipStyle: React.CSSProperties = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 10,
  color: 'hsl(var(--foreground))',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 12px',
};

/** Soft accent band instead of Recharts' default solid white hover rectangle. */
function ConflictBarHoverCursor({
  x,
  y,
  width,
  height,
}: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}) {
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill="#0088cc"
      fillOpacity={0.12}
      rx={4}
      pointerEvents="none"
    />
  );
}

const REC_LABEL: Record<string, string> = {
  merge: 'Merge incoming into registry',
  keep_existing: 'Keep existing registry data',
  create_new: 'Create as new part record',
  review: 'Manual review required',
};

export default function PartFinderIntelligencePage() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 10;
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('id'));
  const [aiLoading, setAiLoading] = useState(false);

  const selected = useMemo(
    () => suggestions.find((s) => s._id === selectedId) || null,
    [suggestions, selectedId]
  );

  const listTotalPages = Math.max(1, Math.ceil(suggestions.length / LIST_PAGE_SIZE));
  const listSafePage = Math.min(listPage, listTotalPages);
  const pagedSuggestions = useMemo(
    () => suggestions.slice((listSafePage - 1) * LIST_PAGE_SIZE, listSafePage * LIST_PAGE_SIZE),
    [suggestions, listSafePage]
  );

  useEffect(() => {
    setListPage(1);
  }, [statusFilter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        authenticatedFetch('/api/v1/admin/product-hierarchy/partfinder/analytics'),
        authenticatedFetch(
          `/api/v1/admin/product-hierarchy/partfinder/suggestions?status=${statusFilter}&limit=100`
        ),
      ]);
      const aJson = await aRes.json();
      const sJson = await sRes.json();
      if (aJson.success) setAnalytics(aJson.data);
      if (sJson.success) {
        setSuggestions(sJson.data.items || []);
        setPendingCount(sJson.data.pendingCount || 0);
      }
    } catch {
      showToast('Failed to load PartFinder intelligence', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) setSelectedId(id);
  }, [searchParams]);

  const selectSuggestion = (id: string) => {
    setSelectedId(id);
    setSearchParams({ id });
  };

  const handleAccept = async (id: string) => {
    const res = await authenticatedFetch(
      `/api/v1/admin/product-hierarchy/partfinder/suggestions/${id}/accept`,
      { method: 'POST', body: JSON.stringify({ note: 'Merged via intelligence console' }) }
    );
    const json = await res.json();
    if (!res.ok) {
      showToast(json.error || 'Accept failed', 'error');
      return;
    }
    showToast('Registry updated with incoming data', 'success');
    await fetchAll();
    setSelectedId(null);
    setSearchParams({});
  };

  const handleReject = async (id: string) => {
    const res = await authenticatedFetch(
      `/api/v1/admin/product-hierarchy/partfinder/suggestions/${id}/reject`,
      { method: 'POST', body: JSON.stringify({ note: 'Dismissed — kept registry data' }) }
    );
    const json = await res.json();
    if (!res.ok) {
      showToast(json.error || 'Reject failed', 'error');
      return;
    }
    showToast('Suggestion dismissed', 'success');
    await fetchAll();
    setSelectedId(null);
    setSearchParams({});
  };

  const rerunAi = async (id: string) => {
    setAiLoading(true);
    try {
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/partfinder/suggestions/${id}/ai-analyze`,
        { method: 'POST' }
      );
      const json = await res.json();
      if (json.success) {
        showToast('AI analysis refreshed', 'success');
        await fetchAll();
      } else {
        showToast(json.error || 'AI analysis unavailable', 'error');
      }
    } finally {
      setAiLoading(false);
    }
  };

  const mismatchCount = selected?.fieldDiffs.filter((d) => !d.match).length ?? 0;

  return (
    <div className="w-full p-6 flex flex-col gap-6 min-h-screen">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-[hsl(var(--border))]">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-1">
            <MdHome className="w-4 h-4" />
            <Link to="/dashboard/admin/product-hierarchy" className="hover:underline">
              Parts Hierarchy
            </Link>
            <span>/</span>
            <span>PartFinder Intelligence</span>
          </div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <MdInsights className="w-7 h-7 text-[#0088cc]" />
            Data Comparison & AI Insights
          </h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Smart deduplication when vendors or other sources add parts. Identical records are
            auto-discarded; variations become review suggestions with field-level diffs and AI
            analysis (Hugging Face when configured).
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-2"
        >
          <MdRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Analytics KPIs */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Pending Review', value: analytics.pending, color: 'text-amber-600' },
            { label: 'Merged', value: analytics.accepted, color: 'text-emerald-600' },
            { label: 'Dismissed', value: analytics.rejected, color: 'text-rose-600' },
            { label: 'Avg Match %', value: `${analytics.avgPendingSimilarity}%`, color: 'text-[#0088cc]' },
            { label: 'Last 7 Days', value: analytics.last7Days, color: 'text-purple-600' },
            { label: 'Identical Skipped', value: analytics.identicalDismissed, color: 'text-[hsl(var(--muted-foreground))]' },
          ].map((k) => (
            <div
              key={k.label}
              className="p-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm"
            >
              <p className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">
                {k.label}
              </p>
              <p className={`text-xl font-black mt-1 ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="p-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
              <MdCompare className="w-4 h-4 text-[#0088cc]" /> Top conflicting fields
            </h3>
            {(analytics.topConflictFields?.length ?? 0) === 0 ? (
              <div className="flex h-[220px] items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
                No conflicting field data yet. Hover details appear once suggestions generate conflicts.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.topConflictFields} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
                  <XAxis dataKey="field" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    cursor={<ConflictBarHoverCursor />}
                    contentStyle={chartTooltipStyle}
                    labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 700, marginBottom: 2 }}
                    itemStyle={{ color: '#0088cc' }}
                    formatter={(value: number | string) => [`${value} conflicts`, 'Count']}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="count" fill="#0088cc" radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="p-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
              <MdStorage className="w-4 h-4 text-teal-600" /> Variations by source
            </h3>
            {(analytics.bySource?.length ?? 0) === 0 ? (
              <div className="flex h-[220px] items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
                No source variation data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={analytics.bySource.map((s) => ({
                      name: s.source?.replace('_', ' ') || 'unknown',
                      value: s.count,
                    }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {analytics.bySource.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    itemStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 mb-8">
        {/* Suggestions list */}
        <div className="xl:col-span-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden shadow-sm">
          <div className="p-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-2">
            <span className="text-sm font-bold">
              Suggestions {pendingCount > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">
                  {pendingCount} pending
                </span>
              )}
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))]"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Merged</option>
              <option value="rejected">Dismissed</option>
            </select>
          </div>
          <div className="min-h-[420px] max-h-[480px] overflow-hidden divide-y divide-[hsl(var(--border))]">
            {loading ? (
              <PageSkeleton variant="panel" className="p-4" />
            ) : suggestions.length === 0 ? (
              <p className="p-6 text-center text-xs text-[hsl(var(--muted-foreground))]">
                No data variations to review. When vendors upload catalogues with differing specs, they
                appear here.
              </p>
            ) : (
              pagedSuggestions.map((s) => {
                const diffs = s.fieldDiffs.filter((d) => !d.match).length;
                const active = s._id === selectedId;
                return (
                  <button
                    key={s._id}
                    type="button"
                    onClick={() => selectSuggestion(s._id)}
                    className={`w-full text-left p-3 transition ${
                      active ? 'bg-[#0088cc]/10 border-l-4 border-[#0088cc]' : 'hover:bg-[hsl(var(--muted))]/30'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-[#0088cc] text-xs">
                        {s.incoming?.impa || '—'}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          s.similarityScore >= 80
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {s.similarityScore}% match
                      </span>
                    </div>
                    <p className="text-xs font-semibold mt-1 truncate">{s.incoming?.description}</p>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                      {diffs} diff(s) · {s.source.vendorName || s.source.type}
                    </p>
                  </button>
                );
              })
            )}
          </div>
          {suggestions.length > LIST_PAGE_SIZE && (
            <div className="p-2 border-t border-[hsl(var(--border))] flex justify-between items-center text-[10px]">
              <span>
                Page {listSafePage}/{listTotalPages}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={listSafePage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded border disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={listSafePage >= listTotalPages}
                  onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
                  className="px-2 py-1 rounded border disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="xl:col-span-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden shadow-sm mb-6">
          {!selected ? (
            <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">
              <MdCompare className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-bold">Select a suggestion to compare registry vs incoming data</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black font-mono text-[#0088cc]">
                    IMPA {selected.incoming?.impa}
                  </h2>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {mismatchCount} field difference(s) · {selected.matchType} match ·{' '}
                    {selected.source.vendorName || selected.source.type}
                  </p>
                </div>
                {selected.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleAccept(selected._id)}
                      className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center gap-1"
                    >
                      <MdCheckCircle className="w-4 h-4" /> Merge into Registry
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReject(selected._id)}
                      className="px-3 py-1.5 rounded-xl border border-rose-200 text-rose-600 text-xs font-bold flex items-center gap-1"
                    >
                      <MdClose className="w-4 h-4" /> Keep Existing
                    </button>
                  </div>
                )}
              </div>

              {/* AI panel */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#0088cc]/10 to-purple-500/10 border border-[#0088cc]/20">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-xs font-black uppercase tracking-wide flex items-center gap-1 text-[#0088cc]">
                    <MdAutoAwesome className="w-4 h-4" /> AI Analysis
                  </h3>
                  <button
                    type="button"
                    onClick={() => rerunAi(selected._id)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 rounded-lg bg-[#0088cc] text-white text-[10px] font-bold hover:bg-[#0077b3] disabled:opacity-50"
                  >
                    {aiLoading ? 'Analyzing…' : 'Re-run AI'}
                  </button>
                </div>
                {selected.aiAnalysis ? (
                  <>
                    <p className="text-sm leading-relaxed">{selected.aiAnalysis.summary}</p>
                    <p className="text-xs font-bold mt-2 text-purple-700">
                      Recommendation: {REC_LABEL[selected.aiAnalysis.recommendation] || selected.aiAnalysis.recommendation}
                      {' '}(confidence {Math.round(selected.aiAnalysis.confidence)}% · {selected.aiAnalysis.model})
                    </p>
                    {selected.aiAnalysis.insights?.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {selected.aiAnalysis.insights.map((ins, i) => (
                          <li key={i} className="text-xs flex items-start gap-1.5">
                            <MdWarning className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                            {ins}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Set HUGGINGFACE_API_KEY for enhanced AI insights. Rule-based analysis runs by default.
                  </p>
                )}
              </div>

              {/* Side-by-side diff table */}
              <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))]">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--muted))]">
                    <tr>
                      <th className="py-2 px-3 text-left font-bold">Field</th>
                      <th className="py-2 px-3 text-left font-bold text-[#0088cc]">PartFinder Registry</th>
                      <th className="py-2 px-3 text-left font-bold text-teal-600">Incoming Data</th>
                      <th className="py-2 px-3 text-center font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--border))]">
                    {selected.fieldDiffs.map((d) => (
                      <tr
                        key={d.field}
                        className={d.match ? '' : 'bg-amber-50/50 dark:bg-amber-950/20'}
                      >
                        <td className="py-2 px-3 font-semibold">
                          {d.label}
                          {!d.match && d.severity === 'critical' && (
                            <span className="ml-1 text-[9px] text-rose-600 font-black">CRITICAL</span>
                          )}
                        </td>
                        <td className="py-2 px-3 font-mono">{d.existingValue}</td>
                        <td className="py-2 px-3 font-mono">{d.incomingValue}</td>
                        <td className="py-2 px-3 text-center">
                          {d.match ? (
                            <span className="text-emerald-600 font-bold">✓</span>
                          ) : (
                            <span className="text-amber-600 font-bold">≠</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
